import { useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'

import Modal from 'modules/core/components/Modal'
import TextArea from 'modules/core/components/TextArea'
import i18n from 'modules/core/lib/i18n'

import { useManagedExpensesLimits } from 'modules/expenses/hooks/useManagedExpensesLimits'

interface ReviewModalProps {
  type: 'approve' | 'reject' | 'request_changes'
  title?: string
  description?: string
  onSubmit: (reason?: string) => void
  onClose: () => void
  spender?: string
}

const ReviewModal = ({
  type,
  title,
  description,
  onSubmit,
  onClose,
  spender,
}: ReviewModalProps) => {
  const { usageLimit, extraUsage, unitPrice, limitReached } = useManagedExpensesLimits()
  const [reason, setReason] = useState<string>()

  const extraPriceMessage =
    limitReached &&
    i18n.t('expenses_v2.list.actions.modal.limit_reached', {
      limit: usageLimit,
      extra_usage: extraUsage,
      unit_price: unitPrice,
    })

  const isReject = type === 'reject'

  let titleLabel
  switch (type) {
    case 'approve':
      titleLabel = title ?? i18n.t('expenses_v2.list.actions.modal.title_approve')
      break
    case 'reject':
      titleLabel = title ?? i18n.t('expenses_v2.list.actions.modal.title_reject_v2')
      break
    case 'request_changes':
      titleLabel = title ?? i18n.t('expenses_v2.list.actions.modal.title_request_changes')
      break
    default:
      titleLabel = title ?? ''
  }

  let descriptionLabel
  switch (type) {
    case 'approve':
      descriptionLabel = description ?? i18n.t('expenses_v2.list.actions.modal.description_approve')
      break
    case 'reject':
      descriptionLabel =
        description ?? i18n.t('expenses_v2.list.actions.modal.description_reject_v2')
      break
    case 'request_changes':
      descriptionLabel =
        description ??
        i18n.t('expenses_v2.list.actions.modal.description_request_changes_v2', {
          author: spender,
        })
      break
    default:
      descriptionLabel = description ?? ''
  }

  let buttonLabel
  switch (type) {
    case 'approve':
      buttonLabel = i18n.t('expenses_v2.list.actions.modal.submit_approve')
      break
    case 'reject':
      buttonLabel = i18n.t('expenses_v2.list.actions.modal.submit_reject')
      break
    case 'request_changes':
      buttonLabel = i18n.t('expenses_v2.list.actions.modal.submit_request_changes')
      break
    default:
      buttonLabel = ''
  }

  const handleSubmit = (reason?: string) => {
    onClose()
    onSubmit(reason)
  }

  return (
    <Modal
      title={titleLabel}
      description={descriptionLabel}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('expenses_v2.list.actions.modal.cancel')}
          </Button>
          <Button onClick={() => handleSubmit(reason)}>{buttonLabel}</Button>
        </>
      }
      onClose={onClose}
      illustration="alert"
    >
      {type != 'approve' && (
        <Box paddingBottom={extraPriceMessage ? 's32' : null}>
          <TextArea
            label={
              isReject
                ? i18n.t('expenses_v2.list.actions.modal.body_reject_v2')
                : i18n.t('expenses_v2.list.actions.modal.body_request_changes_v2')
            }
            value={reason ?? ''}
            onChange={setReason}
            placeholder={
              isReject
                ? i18n.t('expenses_v2.list.actions.modal.body_reject_placeholder_v2')
                : i18n.t('expenses_v2.list.actions.modal.body_request_changes_placeholder')
            }
          />
        </Box>
      )}
      {extraPriceMessage && (
        <Box background="champagne400" padding="s16" borderRadius={{ all: 'abs008' }}>
          <Text.Normal>{extraPriceMessage}</Text.Normal>
        </Box>
      )}
    </Modal>
  )
}

export default ReviewModal
